import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import { Button } from "@app/components/ui/button";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import { Badge } from "@app/components/ui/badge";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/macro";
import {
  User,
  FileText,
  CheckCircle2,
  XCircle,
  UserPlus,
  Search,
  Loader2,
  AlertCircle,
} from "lucide-react";
import API from "@app/services/api";
import { PatientSelectV2 } from "@app/types/patient";

interface IdentityCandidate {
  id: string;
  source: "llm" | "filename" | "manual";
  confidence: number;
  extracted_nom: string | null;
  extracted_prenom: string | null;
  extracted_dob: string | null;
  proposed_patient_id: string | null;
  status: "PENDING" | "CONFIRMED" | "REJECTED" | "NEW_PATIENT";
}

interface RawDocument {
  id: string;
  original_filename: string;
  file_type: string;
  identity_status: string;
  mage_x_patient_id: string | null;
  identity_candidates?: IdentityCandidate[];
}

interface Props {
  docId: string | null;
  onClose: () => void;
  onResolved: (docId: string, newStatus: string) => void;
}

interface DocumentDetailApiResult {
  ok?: boolean;
  data?: RawDocument;
  error?: string;
}

export default function DocumentIdentityReview({
  docId,
  onClose,
  onResolved,
}: Props) {
  const { t } = useLingui();

  const [doc, setDoc] = useState<RawDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [showNewPatientForm, setShowNewPatientForm] = useState(false);
  const [newPatientForm, setNewPatientForm] = useState({ nom: "", prenom: "", date_naissance: "" });

  // Load document + candidates when opened
  useEffect(() => {
    if (!docId) return;
    setLoading(true);
    setError("");
    setSelectedPatientId(null);
    setSearchQuery("");
    setShowNewPatientForm(false);

    API.get({ path: `/documents/${docId}` })
      .then((res) => {
        const r = res as DocumentDetailApiResult;
        if (r.ok && r.data) setDoc(r.data);
        else setError(r.error || t`Erreur lors du chargement`);
      })
      .catch(() => setError(t`Erreur lors du chargement du document`))
      .finally(() => setLoading(false));
  }, [docId, t]);

  // Patient search — server-side (debounced) via /patient/all-for-select, which
  // performs a case-insensitive name match scoped to the caller's patients. This
  // replaces a former client-side filter over the whole in-memory patient list
  // (which required bulk-downloading every patient); it also works when the user
  // reaches this modal without having visited the dashboard first.
  const [filteredPatients, setFilteredPatients] = useState<PatientSelectV2[]>([]);

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setFilteredPatients([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      API.get({ path: "/patient/all-for-select", query: { search: q } })
        .then((res: { ok?: boolean; data?: { patients?: PatientSelectV2[] } }) => {
          if (cancelled) return;
          setFilteredPatients(res.ok && res.data?.patients ? res.data.patients.slice(0, 10) : []);
        })
        .catch(() => {
          if (!cancelled) setFilteredPatients([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [searchQuery]);

  const handleConfirmCandidate = async (candidate: IdentityCandidate) => {
    if (!doc) return;
    setSaving(true);
    setError("");
    try {
      const res = (await API.post({
        path: `/documents/${doc.id}/resolve-identity`,
        body: {
          candidate_id: candidate.id,
          action: "confirm",
        },
      })) as { ok: boolean; data?: { identity_status: string }; error?: string };
      if (res.ok && res.data) {
        onResolved(doc.id, res.data.identity_status);
        onClose();
      } else {
        setError(res.error || t`Erreur lors de la confirmation`);
      }
    } catch {
      setError(t`Erreur lors de la confirmation`);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmSearch = async () => {
    if (!doc || !selectedPatientId) return;
    setSaving(true);
    setError("");
    try {
      const res = (await API.post({
        path: `/documents/${doc.id}/resolve-identity`,
        body: { action: "confirm", patient_id: selectedPatientId },
      })) as { ok: boolean; data?: { identity_status: string }; error?: string };
      if (res.ok && res.data) {
        onResolved(doc.id, res.data.identity_status);
        onClose();
      } else {
        setError(res.error || t`Erreur lors de la liaison`);
      }
    } catch {
      setError(t`Erreur lors de la liaison`);
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePatient = async () => {
    if (!doc || !newPatientForm.nom || !newPatientForm.prenom) return;
    setSaving(true);
    setError("");
    try {
      const res = (await API.post({
        path: `/documents/${doc.id}/resolve-identity`,
        body: {
          action: "new_patient",
          nom: newPatientForm.nom,
          prenom: newPatientForm.prenom,
          date_naissance: newPatientForm.date_naissance || null,
        },
      })) as { ok: boolean; data?: { identity_status: string }; error?: string };
      if (res.ok && res.data) {
        onResolved(doc.id, res.data.identity_status);
        onClose();
      } else {
        setError(res.error || t`Erreur lors de la création`);
      }
    } catch {
      setError(t`Erreur lors de la création du patient`);
    } finally {
      setSaving(false);
    }
  };

  const handleIgnore = async () => {
    if (!doc) return;
    setSaving(true);
    try {
      const res = (await API.post({
        path: `/documents/${doc.id}/resolve-identity`,
        body: { action: "ignore" },
      })) as { ok: boolean; data?: { identity_status: string }; error?: string };
      if (res.ok && res.data) {
        onResolved(doc.id, res.data.identity_status);
        onClose();
      }
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const confidenceBadge = (confidence: number) => {
    const pct = Math.round(confidence * 100);
    const cls =
      pct >= 80
        ? "bg-green-100 text-green-700"
        : pct >= 60
          ? "bg-blue-100 text-blue-700"
          : "bg-slate-100 text-slate-600";
    return (
      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
        {pct}%
      </span>
    );
  };

  const sourceLabel = (source: string) => {
    if (source === "llm") return t`IA`;
    if (source === "filename") return t`Nom du fichier`;
    return t`Manuel`;
  };

  const pendingCandidates =
    doc?.identity_candidates?.filter((c) => c.status === "PENDING") ?? [];

  return (
    <Dialog open={!!docId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-4 w-4 text-primary" />
            <Trans>Identifier le patient</Trans>
          </DialogTitle>
          <DialogDescription>
            {doc ? (
              <span className="flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" />
                {doc.original_filename}
              </span>
            ) : (
              <Trans>Chargement…</Trans>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Candidates from pipeline */}
            {pendingCandidates.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">
                  <Trans>Correspondances suggérées</Trans>
                </p>
                {pendingCandidates.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5 hover:border-primary/30 hover:bg-primary/5 transition-colors"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <User className="h-4 w-4 text-slate-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800">
                          {c.extracted_nom} {c.extracted_prenom}
                        </p>
                        {c.extracted_dob && (
                          <p className="text-xs text-slate-500">
                            {c.extracted_dob.substring(0, 4)}
                          </p>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {sourceLabel(c.source)}
                      </Badge>
                      {confidenceBadge(c.confidence)}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="ml-2 shrink-0 gap-1 border-primary/30 text-primary hover:bg-primary hover:text-white"
                      onClick={() => handleConfirmCandidate(c)}
                      disabled={saving}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      <Trans>Confirmer</Trans>
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Manual search */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">
                <Trans>Chercher un patient existant</Trans>
              </Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                <Input
                  className="pl-8"
                  placeholder={t`Nom ou prénom…`}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSelectedPatientId(null);
                  }}
                />
              </div>
              {filteredPatients.length > 0 && (
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-40 overflow-y-auto">
                  {filteredPatients.map((p) => (
                    <button
                      key={p.m}
                      onClick={() => {
                        // Link by id_magex (unique). id_patient (p.i) is NOT
                        // unique and would risk binding the doc to the wrong patient.
                        setSelectedPatientId(p.m);
                        setSearchQuery(p.n);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50 transition-colors ${
                        selectedPatientId === p.m
                          ? "bg-primary/5 text-primary"
                          : "text-slate-700"
                      }`}
                    >
                      <User className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                      <span>{p.n}</span>
                    </button>
                  ))}
                </div>
              )}
              {selectedPatientId && (
                <Button
                  size="sm"
                  className="w-full gap-1.5"
                  onClick={handleConfirmSearch}
                  disabled={saving}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <Trans>Lier à ce patient</Trans>
                </Button>
              )}
            </div>

            {/* New patient form */}
            {!showNewPatientForm ? (
              <button
                onClick={() => setShowNewPatientForm(true)}
                className="flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <UserPlus className="h-3.5 w-3.5" />
                <Trans>Créer un nouveau patient</Trans>
              </button>
            ) : (
              <div className="rounded-lg border border-slate-200 p-3 space-y-3">
                <p className="text-sm font-medium text-slate-700">
                  <Trans>Nouveau patient</Trans>
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs"><Trans>Nom</Trans></Label>
                    <Input
                      placeholder={t`Ex. : DUPONT`}
                      value={newPatientForm.nom}
                      onChange={(e) =>
                        setNewPatientForm((p) => ({ ...p, nom: e.target.value.toUpperCase() }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs"><Trans>Prénom</Trans></Label>
                    <Input
                      placeholder={t`Ex. : Jean`}
                      value={newPatientForm.prenom}
                      onChange={(e) =>
                        setNewPatientForm((p) => ({ ...p, prenom: e.target.value }))
                      }
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    <Trans>Date de naissance</Trans>{" "}
                    <span className="text-slate-400">(<Trans>optionnel</Trans>)</span>
                  </Label>
                  <Input
                    type="date"
                    value={newPatientForm.date_naissance}
                    onChange={(e) =>
                      setNewPatientForm((p) => ({ ...p, date_naissance: e.target.value }))
                    }
                  />
                </div>
                <Button
                  size="sm"
                  className="w-full gap-1.5"
                  onClick={handleCreatePatient}
                  disabled={saving || !newPatientForm.nom || !newPatientForm.prenom}
                >
                  {saving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <UserPlus className="h-3.5 w-3.5" />
                  )}
                  <Trans>Créer et lier</Trans>
                </Button>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-1.5 text-sm text-red-600">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                {error}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" size="sm" onClick={handleIgnore} disabled={saving}>
            <XCircle className="h-3.5 w-3.5 mr-1" />
            <Trans>Ignorer</Trans>
          </Button>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            <Trans>Fermer</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
